import { SubmissionStatus } from '@bp/bp-graphql-types';
import { ArrowRightIcon, Button, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { ActivityProtocol } from 'components/ActivityProtocol/ActivityProtocol';
import { AssignmentMaterialCard } from 'components/Assignments/components/AssignmentMaterialCard/AssignmentMaterialCard';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { BpCard } from 'components/BpCard/BpCard';
import { BpHeader } from 'components/BpHeader/BpHeader';
import { SubmissionAnswerCardStudent } from 'components/Submissions/components/SubmissionAnswerCard/SubmissionAnswerCardStudent';
import { SubmissionFeedbackCard } from 'components/Submissions/components/SubmissionFeedbackCard/SubmissionFeedbackCard';
import { SubmissionInfoCard } from 'components/Submissions/components/SubmissionInfoCard/SubmissionInfoCard';
import { UpdateSubmissionsMutation, useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { SubmissionAnswerModal } from 'components/Submissions/modals/SubmissionAnswerModal';
import { SubmissionEditRequestModal } from 'components/Submissions/modals/SubmissionEditRequestModal';
import { useActivityProtocol } from 'hooks/matrix/useActivityProtocol';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useRequireParams } from 'hooks/useRequireParams';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { NotificationReason } from 'utils/matrixClient';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';

type CourseSubmissionStudentContentProps = {
  assignmentUuid: string;
  onNavigate: (assignmentUuid: string) => void;
};

export const CourseSubmissionStudentContent = ({ assignmentUuid, onNavigate }: CourseSubmissionStudentContentProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { sendMessage } = useActivityProtocol();

  const [, updateSubmission] = useMutation(UpdateSubmissionsMutation);

  const [answerLoading, setAnswerLoading] = useState<boolean>(false);

  const { assignments } = useAssignments({ courseUuid, assignmentUuid });
  const { submissions, ConfirmSubmitDialog, confirmSubmit } = useSubmissions({ courseUuid, assignmentUuid });

  const [answerModalOpen, setAnswerModalOpen] = useState<boolean>(false);
  const [requestModalOpen, setRequestModalOpen] = useState<boolean>(false);

  const assignment = assignments.find((a) => a.uuid === assignmentUuid);
  if (!assignment) throw new Error('Assignment not found');

  const submission = submissions.find((s) => s.assignment.uuid === assignment.uuid && s.owner.uuid === profileUuid);
  if (!submission) throw new Error('Submission not found');

  const publicSubmissions = submissions.filter((s) => s.assignment.uuid === assignment.uuid && s.public);

  const hasFeedback = !!submission.feedbackMaterial;

  function handleRequestEdit() {
    setRequestModalOpen(true);
  }

  function handleEdit() {
    setAnswerModalOpen(true);
  }

  async function handleSubmit() {
    if (!submission) return;

    const confirm = await confirmSubmit();
    if (!confirm) return;

    setAnswerLoading(true);

    const { error } = await updateSubmission({
      where: {
        uuid: submission.uuid,
      },
      update: {
        status: SubmissionStatus.New,
      },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
      await sendMessage(submission.uuid, NotificationReason.SubmissionNew);
    }

    setAnswerLoading(false);
  }

  function handleClose() {
    setRequestModalOpen(false);
    setAnswerModalOpen(false);
  }

  return (
    <>
      <BpHeader headline={assignment.title} showBackButton />

      <Grid>
        <GridRow>
          <GridColumn width={8}>
            <GridRow>
              <AssignmentMaterialCard assignmentUuid={assignmentUuid} />
            </GridRow>
            <GridRow>
              <SubmissionAnswerCardStudent
                assignmentUuid={assignmentUuid}
                loading={answerLoading}
                onRequestEdit={() => handleRequestEdit()}
                onEdit={() => handleEdit()}
                onSubmit={() => handleSubmit()}
              />
            </GridRow>
            {hasFeedback && (
              <GridRow>
                <SubmissionFeedbackCard submissionUuid={submission.uuid} />
              </GridRow>
            )}
          </GridColumn>

          <GridColumn width={4}>
            <GridRow>
              <SubmissionInfoCard submissionUuid={submission.uuid} />
            </GridRow>

            {publicSubmissions.length > 0 && (
              <GridRow>
                <BpCard noPadding>
                  <Button
                    icon={<ArrowRightIcon />}
                    iconPosition='right'
                    fullWidth
                    hierarchy='tertiary'
                    style={{ fontSize: 'var(--font-size-m)', padding: '0 var(--spacing-5)' }}
                    onClick={() => onNavigate(assignmentUuid)}
                  >
                    {t('submissions.showPublicSubmissions', { available: publicSubmissions.length })}
                  </Button>
                </BpCard>
              </GridRow>
            )}

            <GridRow>
              <BpCard
                alwaysCollapsible
                header={{
                  headline: t('activityProtocol.title'),
                }}
              >
                <ActivityProtocol submissionUuid={submission.uuid} />
              </BpCard>
            </GridRow>
          </GridColumn>
        </GridRow>
      </Grid>

      <SubmissionEditRequestModal
        submissionUuid={submission.uuid}
        isOpen={requestModalOpen}
        onClose={() => handleClose()}
      />

      <SubmissionAnswerModal submissionUuid={submission.uuid} isOpen={answerModalOpen} onClose={() => handleClose()} />

      <ConfirmSubmitDialog />
    </>
  );
};
